import { useNavigate } from 'react-router-dom';
import { ReactComponent as RotateIcon } from '@assets/icons/rotate_icon.svg';
import StatusChip from '@components/widgets/StatusChip';
import Button from '@components/forms/Button';
import Table, { TableHeader } from '@components/widgets/Table';
import { format, parseISO } from 'date-fns';
import { useState, useEffect } from 'react';
import { Agreement } from '@types';
import { formatCurrency, formatKilometers } from '@utils';

interface AgreementTableProps {
  data: Agreement[];
  loadMoreData: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

const AgreementTable = ({
  data,
  loadMoreData,
  hasMore,
  isLoading
}: AgreementTableProps) => {
  const navigate = useNavigate();
  const [visibleItems, setVisibleItems] = useState(40);

  const handleRowClick = (record: Agreement) => {
    navigate(`/agreements/detail/${record.buybackId}`);
  };

  const loadMore = () => {
    loadMoreData();
    setVisibleItems((prevVisibleItems: number) => prevVisibleItems + 40);
  };

  const visibleData = data.slice(0, visibleItems);

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in life':
        return 'success';
      case 'quoted':
        return 'warning';
      case 'completed':
        return 'success1';
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };

  const TABLE_HEAD: TableHeader[] = [
    {
      label: 'Agreement ID',
      field: 'friendlyId'
    },
    {
      label: 'Vehicle',
      field: 'vehicle',
      render: (row: Agreement) => (
        <div>
          <div className="font-medium">
            {row.year} {row.make} {row.model}
          </div>
        </div>
      )
    },
    {
      label: 'Start Date',
      field: 'startDate',
      render: (row: Agreement) => {
        const formattedDate =
          row.startDate && format(parseISO(row.startDate), 'd MMM yyyy');
        return <span>{formattedDate}</span>;
      }
    },
    {
      label: 'End Date',
      field: 'endDate',
      render: (row: Agreement) => {
        const formattedDate =
          row.endDate && format(parseISO(row.endDate), 'd MMM yyyy');
        return <span>{formattedDate}</span>;
      }
    },
    {
      label: 'Term',
      field: 'termMonths',
      render: (row: Agreement) => `${row.termMonths} months`
    },
    {
      label: 'Total KMs',
      field: 'totalKms',
      render: (row: Agreement) => formatKilometers(row.totalKms)
    },
    {
      label: 'Buyback Amount',
      field: 'buybackAmount',
      render: (row: Agreement) => (
        <span className="font-medium text-green-600">
          {formatCurrency(row.buybackAmount)}
        </span>
      )
    },
    {
      label: 'Status',
      field: 'status',
      render: (row: Agreement) => (
        <StatusChip variant={getStatusVariant(row.status)} label={row.status} />
      )
    }
  ];

  const [maxHeight, setMaxHeight] = useState(600);

  useEffect(() => {
    // Update maxHeight when window resizes
    const handleResize = () => {
      setMaxHeight(window.innerHeight - 320);
    };

    // Set initial maxHeight
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div>
      <div>
        <Table
          headers={TABLE_HEAD}
          data={visibleData}
          onClickRow={(row) => handleRowClick(row)}
          maxHeight={maxHeight}
        />
      </div>
      {hasMore && visibleItems <= data.length && (
        <div className="flex justify-center pt-6">
          <Button
            className="bg-white border-grey-300 text-grey-600 text-sm flex items-center gap-2"
            variant="outlined"
            onClick={loadMore}
            disabled={isLoading}>
            <RotateIcon className={isLoading ? 'animate-spin' : ''} />
            {isLoading ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AgreementTable;
